import type { Role, RateMultiplier, ExtendedTier } from '@/types';
import { normalizeExtendedTiers } from '@/utils/extendedOvertime';

export interface RateCalculationContext {
  roles: Role[];
  rateMultipliers: RateMultiplier[];
  minimumBillableHours?: number | null;
  /** When set, calculations use this role instead of the default getSampleRole(). */
  overrideRoleIndex?: number;
}

export interface PayCalculationResult {
  totalPay: number;
  premiumPay: number;
  straightTimeHours: number;
  overtimeHours: number;
}

export interface PayBucket {
  hours: number;
  tierName: string;
  multiplier: number;
  amount: number;
  annotation?: string;
  lifted?: boolean;
  liftedFrom?: string;
  /** When set, replaces the default "{hours}h × {tierName} ({multiplier}x)" label */
  customLabel?: string;
}

export interface RateProgressionResult {
  progression: string;
  finalRate: string;
  finalRateCode: string;
  progressionWithPay: string;
  startingRate: string;
  startingRateCode: string;
  segments: PayBucket[];
}

export interface SurchargeOptions {
  mode?: 'carry_over' | 'bump_up' | 'offset_overtime' | 'fixed_dollar' | 'hourly_premium' | 'guaranteed_rate';
  previousShiftFinalRateCode?: string;
  fixedStartingMultiplierCode?: string;
  fixedSurchargeAmount?: number;
  premiumType?: 'percentage' | 'fixed';
  premiumValue?: number;
  surchargeAppliesToShortedHoursOnly?: boolean;
  premiumBasis?: 'base_rate' | 'ot_inclusive';
  violationHours?: number;
  startingHoursInWeek?: number;
  extendedOvertimeTiers?: ExtendedTier[];
}

/**
 * Formats a rate multiplier for display
 */
export function formatMultiplier(m: RateMultiplier | { code?: string; name: string; defaultMultiplier: number }): string {
  return `${m.name} (${m.defaultMultiplier}x)`;
}

/**
 * Formats a rate multiplier for display with pay amount
 */
export function formatMultiplierWithPay(
  m: RateMultiplier | { code?: string; name: string; defaultMultiplier: number },
  hours: number,
  hourlyRate: number
): string {
  const pay = hours * hourlyRate * (m.defaultMultiplier ?? 0);
  const fractionDigits = pay % 1 === 0 ? 0 : 2;
  return `${m.name} ($${pay.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: 2 })})`;
}

/**
 * Gets the threshold for a rate multiplier based on billing structure
 */
export function getThreshold(
  m: RateMultiplier | { code?: string } | undefined, 
  billingStructure: 'day_rate' | 'hourly' | null
): number {
  if (!m || m.code === 'STRAIGHT_TIME') return 0;
  const multiplier = m as RateMultiplier;
  const threshold = billingStructure === 'day_rate' 
    ? multiplier.defaultPerShiftThresholdHours 
    : multiplier.defaultPerWeekThresholdHours;
  return threshold ?? 0;
}

/**
 * Finds a sample role, preferring the most common billing structure.
 */
export function getSampleRole(roles: Role[]): Role {
  if (!roles || roles.length === 0) {
    return { name: 'Sample Role', rate: 50, billingStructure: 'hourly' as const, allowHalfDay: false, halfDayMethod: null, halfDayMultiplier: null, halfDayFixedRate: null, halfDayFixedDiscount: null };
  }

  const dayRateCount = roles.filter(r => r.billingStructure === 'day_rate').length;
  const hourlyCount = roles.length - dayRateCount;

  if (hourlyCount > dayRateCount) {
    // Prefer hourly if it's more common
    return roles.find(r => r.billingStructure === 'hourly')!;
  }
  
  // Otherwise, prefer day rate (or if counts are equal)
  return roles.find(r => r.billingStructure === 'day_rate') || roles[0];
}

/** Filters roles to those with a name and positive rate. */
export function getRatedRoles(roles: Role[]): Role[] {
  return roles.filter(r => r.name && r.rate && r.rate > 0);
}

/** Resolves the sample role from context, respecting overrideRoleIndex if set. */
export function resolveRole(context: RateCalculationContext): Role {
  if (context.overrideRoleIndex != null) {
    const ratedRoles = getRatedRoles(context.roles);
    if (context.overrideRoleIndex >= 0 && context.overrideRoleIndex < ratedRoles.length) {
      return ratedRoles[context.overrideRoleIndex];
    }
  }
  return getSampleRole(context.roles);
}


/**
 * Gets the straight time rate multiplier
 */
export function getStraightTimeMultiplier(rateMultipliers: RateMultiplier[]): RateMultiplier {
  return rateMultipliers.find(m => m.code === 'STRAIGHT_TIME') || { 
    code: 'STRAIGHT_TIME', 
    name: 'Straight Time',
    defaultMultiplier: 1.0,
    perShiftBillingStructures: ['hourly', 'day_rate'],
    perWeekBillingStructures: ['hourly', 'day_rate']
  };
}

/**
 * Gets sorted overtime multipliers for a given billing structure
 */
export function getSortedOvertimeMultipliers(
  rateMultipliers: RateMultiplier[],
  billingStructure: 'day_rate' | 'hourly' | null
): RateMultiplier[] {
  if (!billingStructure) {
    return [];
  }

  // Return all multipliers that have per-shift thresholds, regardless of
  // perShiftBillingStructures. The backend applies the same overtime ladder
  // universally — billing structure only determines which threshold column
  // to prefer (per-shift vs per-week), not whether the multiplier applies.
  const dailyLadder = rateMultipliers
    .filter(m =>
      m.code !== 'STRAIGHT_TIME' &&
      m.defaultPerShiftThresholdHours != null &&
      m.defaultPerShiftThresholdHours >= 0
    )
    .sort((a, b) => a.defaultPerShiftThresholdHours! - b.defaultPerShiftThresholdHours!);

  if (dailyLadder.length > 0) {
    return dailyLadder;
  }

  // If no daily ladder, fall back to weekly thresholds.
  const weeklyLadder = rateMultipliers
    .filter(m =>
      m.code !== 'STRAIGHT_TIME' &&
      m.defaultPerWeekThresholdHours != null &&
      m.defaultPerWeekThresholdHours >= 0
    )
    .sort((a, b) => a.defaultPerWeekThresholdHours! - b.defaultPerWeekThresholdHours!);

  return weeklyLadder;
}

/**
 * Calculates pay for given hours with short turn surcharge applied
 */
export function calculatePayWithSurcharge(
  hours: number,
  context: RateCalculationContext,
  surchargeOptions: SurchargeOptions = {}
): PayCalculationResult {
  const { rateMultipliers } = context;
  const sampleRole = resolveRole(context);
  const exampleRate = sampleRole.rate;
  const isDayRate = sampleRole.billingStructure === 'day_rate';
  const startingHoursInWeek = surchargeOptions.startingHoursInWeek || 0;

  // For fixed_dollar surcharge, calculate base pay and add fixed amount
  if (surchargeOptions.mode === 'fixed_dollar' && surchargeOptions.fixedSurchargeAmount) {
    const basePay = calculatePay(hours, context, startingHoursInWeek);
    return {
      ...basePay,
      totalPay: basePay.totalPay + surchargeOptions.fixedSurchargeAmount
    };
  }

  // For hourly_premium surcharge, calculate base pay and add uplift
  if (surchargeOptions.mode === 'hourly_premium') {
    const { rateMultipliers, minimumBillableHours } = context;
    const dayRateGuaranteedHours = isDayRate ? (minimumBillableHours ?? 8) : 0;
    const hourlyRateForCalc = isDayRate ? (dayRateGuaranteedHours > 0 ? exampleRate / dayRateGuaranteedHours : exampleRate) : exampleRate;

    const basePay = calculatePay(hours, context, startingHoursInWeek);
    let premium = 0;

    const violationOnly = surchargeOptions.surchargeAppliesToShortedHoursOnly ?? false;
    const effectiveHours = violationOnly && surchargeOptions.violationHours
      ? Math.min(hours, surchargeOptions.violationHours)
      : hours;

    if (surchargeOptions.premiumBasis === 'ot_inclusive') { // Compounding logic
      const ladderBillingStructure = rateMultipliers.some(m => m.defaultPerShiftThresholdHours != null) ? 'day_rate' : 'hourly';
      const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
      const ladder = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);
      const originalMultipliers = [straightTimeMultiplier, ...ladder];

      for (let i = 0; i < originalMultipliers.length; i++) {
        const multiplier = originalMultipliers[i];
        const bracketStart = getThreshold(multiplier, ladderBillingStructure);
        if (effectiveHours <= bracketStart) break;

        const nextBracketStart = (i + 1 < originalMultipliers.length) ? getThreshold(originalMultipliers[i + 1], ladderBillingStructure) : Infinity;
        const hoursInBracket = Math.min(effectiveHours, nextBracketStart) - bracketStart;

        if(hoursInBracket <= 0) continue;

        const grossPayInBracket = hoursInBracket * hourlyRateForCalc * multiplier.defaultMultiplier!;

        if (surchargeOptions.premiumType === 'percentage' && surchargeOptions.premiumValue) {
          premium += grossPayInBracket * (surchargeOptions.premiumValue / 100);
        } else if (surchargeOptions.premiumType === 'fixed') {
          // Fixed (Dollar) premium compounds by the active overtime multiplier
          // when OT-inclusive basis is enabled. Use $15/hr fallback when empty.
          const baseRatePerHour = (Number(surchargeOptions.premiumValue) || 15);
          const multiplierRate = multiplier.defaultMultiplier ?? 1;
          premium += hoursInBracket * baseRatePerHour * multiplierRate;
        }
      }
    } else { // Additive (original) logic
      if (surchargeOptions.premiumType === 'percentage' && surchargeOptions.premiumValue) {
        premium = effectiveHours * hourlyRateForCalc * (surchargeOptions.premiumValue / 100);
      } else if (surchargeOptions.premiumType === 'fixed') {
        const ratePerHour = (Number(surchargeOptions.premiumValue) || 15);
        premium = ratePerHour * effectiveHours;
      }
    }

    return {
      ...basePay,
      totalPay: basePay.totalPay + premium,
      premiumPay: basePay.premiumPay + premium
    };
  }

  // For offset modes and multiplier modes, we need to recalculate with surcharge progression
  if (['carry_over', 'bump_up', 'offset_overtime', 'guaranteed_rate'].includes(surchargeOptions.mode || '')) {
    return calculatePayWithSurchargeProgression(hours, context, surchargeOptions);
  }

  // Default to straight time pay calculation
  return calculatePay(hours, context, startingHoursInWeek);
}

/**
 * Calculates pay with surcharge progression applied (for offset and multiplier modes)
 */
function calculatePayWithSurchargeProgression(
  hours: number,
  context: RateCalculationContext,
  surchargeOptions: SurchargeOptions
): PayCalculationResult {
  const { rateMultipliers, minimumBillableHours } = context;
  const sampleRole = resolveRole(context);
  const exampleRate = sampleRole.rate;
  const isDayRate = sampleRole.billingStructure === 'day_rate';

  const hasDailyThresholds = rateMultipliers.some(m => m.defaultPerShiftThresholdHours != null && m.defaultPerShiftThresholdHours > 0);
  const ladderBillingStructure = hasDailyThresholds ? 'day_rate' : 'hourly';

  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);
  const originalMultipliers = [straightTimeMultiplier, ...ladder];

  const ladderTopMultiplier = Math.max(0, ...originalMultipliers.map(m => m.defaultMultiplier ?? 0));
  const normalizedExtended = normalizeExtendedTiers(
    surchargeOptions.extendedOvertimeTiers ?? [],
    ladderTopMultiplier,
  );
  const lookupLadder: RateMultiplier[] = [
    ...originalMultipliers,
    ...normalizedExtended.map((t): RateMultiplier => ({
      code: `__ext_${t.sortOrder}`,
      name: t.name,
      defaultMultiplier: t.rate,
      defaultPerShiftThresholdHours: null,
      defaultPerWeekThresholdHours: null,
      perShiftBillingStructures: null,
      perWeekBillingStructures: null,
    })),
  ];

  const dayRateGuaranteedHours = isDayRate ? (minimumBillableHours ?? 8) : 0;
  const hourlyRateForCalc = isDayRate ? (dayRateGuaranteedHours > 0 ? exampleRate / dayRateGuaranteedHours : exampleRate) : exampleRate;

  // Determine effective starting index based on surcharge mode
  let effectiveStartIndex = 0;
  if (surchargeOptions.mode === 'offset_overtime' && surchargeOptions.fixedStartingMultiplierCode) {
    const fixedIndex = originalMultipliers.findIndex(m => m.code === surchargeOptions.fixedStartingMultiplierCode);
    if (fixedIndex > 0) {
      effectiveStartIndex = fixedIndex;
    }
  } else if (surchargeOptions.mode && surchargeOptions.previousShiftFinalRateCode && (surchargeOptions.mode === 'carry_over' || surchargeOptions.mode === 'bump_up')) {
    const prevIndex = originalMultipliers.findIndex(m => m.code === surchargeOptions.previousShiftFinalRateCode);
    
    if (prevIndex !== -1) {
      if (surchargeOptions.mode === 'carry_over') {
        effectiveStartIndex = (prevIndex === 0) ? 1 : prevIndex;
      } else if (surchargeOptions.mode === 'bump_up') {
        effectiveStartIndex = prevIndex + 1;
      }
    }
  }
  effectiveStartIndex = Math.min(effectiveStartIndex, originalMultipliers.length - 1);

  let totalPay = 0;
  let premiumPay = 0;
  let straightTimeHours = 0;
  let overtimeHours = 0;

  // For daily ladders, thresholds reset each shift/day; for weekly ladders, include the weekly offset.
  const shiftStartBase = ladderBillingStructure === 'hourly' ? (surchargeOptions.startingHoursInWeek || 0) : 0;
  const violationOnly = surchargeOptions.surchargeAppliesToShortedHoursOnly ?? false;
  const violationHoursAmount = (violationOnly && surchargeOptions.violationHours) ? surchargeOptions.violationHours : hours;

  // Calculate pay for each bracket with surcharge progression
  for (let i = 0; i < originalMultipliers.length; i++) {
    const naturalMultiplier = originalMultipliers[i];
    const bracketStart = getThreshold(naturalMultiplier, ladderBillingStructure);
    const shiftStartHour = shiftStartBase;
    const shiftEndHour = shiftStartBase + hours;

    if (shiftEndHour <= bracketStart) continue;

    const nextBracketStart = (i + 1 < originalMultipliers.length)
      ? getThreshold(originalMultipliers[i + 1], ladderBillingStructure)
      : Infinity;

    const startOfOverlap = Math.max(shiftStartHour, bracketStart);
    const endOfOverlap = Math.min(shiftEndHour, nextBracketStart);
    const hoursInBracket = Math.max(0, endOfOverlap - startOfOverlap);

    if (hoursInBracket <= 0) continue;
    
    // Split hours in bracket into penalized and normal
    const penalizedHoursStart = shiftStartBase;
    const penalizedHoursEnd = shiftStartBase + violationHoursAmount;

    const penalizedOverlapStart = Math.max(startOfOverlap, penalizedHoursStart);
    const penalizedOverlapEnd = Math.min(endOfOverlap, penalizedHoursEnd);
    const penalizedHours = Math.max(0, penalizedOverlapEnd - penalizedOverlapStart);

    const normalHours = hoursInBracket - penalizedHours;
    
    // Calculate for penalized hours
    if (penalizedHours > 0) {
      let multiplierToUse = naturalMultiplier;
      if (surchargeOptions.mode === 'carry_over' || surchargeOptions.mode === 'bump_up' || surchargeOptions.mode === 'offset_overtime') {
        const effectiveMultiplierIndex = i + effectiveStartIndex;
        if (effectiveMultiplierIndex < lookupLadder.length) {
          multiplierToUse = lookupLadder[effectiveMultiplierIndex];
        } else {
          multiplierToUse = lookupLadder[lookupLadder.length - 1];
        }
      } else if (surchargeOptions.mode === 'guaranteed_rate' && surchargeOptions.fixedStartingMultiplierCode) {
        const surchargeFloorMultiplier = rateMultipliers.find(m => m.code === surchargeOptions.fixedStartingMultiplierCode);
        if (surchargeFloorMultiplier && naturalMultiplier.defaultMultiplier! < surchargeFloorMultiplier.defaultMultiplier!) {
          multiplierToUse = surchargeFloorMultiplier;
        }
      }

      const bracketPay = penalizedHours * hourlyRateForCalc * multiplierToUse.defaultMultiplier!;
      totalPay += bracketPay;

      if (multiplierToUse.defaultMultiplier! > 1.0) {
        premiumPay += penalizedHours * hourlyRateForCalc * (multiplierToUse.defaultMultiplier! - 1.0);
        overtimeHours += penalizedHours;
      } else {
        straightTimeHours += penalizedHours;
      }
    }

    // Calculate for normal hours
    if (normalHours > 0) {
      const multiplierToUse = naturalMultiplier;
      const bracketPay = normalHours * hourlyRateForCalc * multiplierToUse.defaultMultiplier!;
      totalPay += bracketPay;

      if (multiplierToUse.defaultMultiplier! > 1.0) {
        premiumPay += normalHours * hourlyRateForCalc * (multiplierToUse.defaultMultiplier! - 1.0);
        overtimeHours += normalHours;
      } else {
        straightTimeHours += normalHours;
      }
    }
  }

  // For day rate, ensure minimum guaranteed pay
  if (isDayRate && totalPay < exampleRate) {
    const guaranteedPremium = exampleRate - totalPay;
    totalPay = exampleRate;
    premiumPay += guaranteedPremium;
  }

  return {
    totalPay,
    premiumPay,
    straightTimeHours,
    overtimeHours
  };
}

/**
 * Calculates pay for given hours, considering overtime thresholds
 */
export function calculatePay(
  hours: number,
  context: RateCalculationContext,
  startingHoursInWeek: number = 0
): PayCalculationResult {
  const { rateMultipliers, minimumBillableHours } = context;
  const sampleRole = resolveRole(context);
  const exampleRate = sampleRole.rate;
  const isDayRate = sampleRole.billingStructure === 'day_rate';

  const hasDailyThresholds = rateMultipliers.some(m => m.defaultPerShiftThresholdHours != null && m.defaultPerShiftThresholdHours > 0);
  const ladderBillingStructure = hasDailyThresholds ? 'day_rate' : 'hourly';

  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, sampleRole.billingStructure);
  const originalMultipliers = [straightTimeMultiplier, ...ladder];

  const dayRateGuaranteedHours = isDayRate ? (minimumBillableHours ?? 8) : 0;
  const hourlyRateForCalc = isDayRate ? (dayRateGuaranteedHours > 0 && isFinite(dayRateGuaranteedHours) ? exampleRate / dayRateGuaranteedHours : exampleRate) : exampleRate;

  if (ladder.length === 0) {
    let totalPay = 0;
    let premiumPay = 0;
    let straightTimeHours = hours;
    let overtimeHours = 0;

    if (isDayRate) {
      const extraHours = Math.max(0, hours - dayRateGuaranteedHours);
      const extraPay = extraHours * hourlyRateForCalc;
      totalPay = exampleRate + extraPay;
      premiumPay = extraPay;
      straightTimeHours = Math.min(hours, dayRateGuaranteedHours);
      overtimeHours = extraHours;
    } else {
      totalPay = hours * exampleRate;
    }
    return {
      totalPay,
      premiumPay,
      straightTimeHours,
      overtimeHours,
    };
  }

  let totalPay = 0;
  let premiumPay = 0;
  let straightTimeHours = 0;
  let overtimeHours = 0;

  // For daily ladders, thresholds reset each shift/day; for weekly ladders, include the weekly offset.
  const shiftStartBase = ladderBillingStructure === 'hourly' ? startingHoursInWeek : 0;

  // Calculate pay for each bracket
  for (let i = 0; i < originalMultipliers.length; i++) {
    const multiplier = originalMultipliers[i];
    const bracketStart = getThreshold(multiplier, ladderBillingStructure);
    const shiftStartHour = shiftStartBase;
    const shiftEndHour = shiftStartBase + hours;

    if (shiftEndHour <= bracketStart) continue;

    const nextBracketStart = (i + 1 < originalMultipliers.length)
      ? getThreshold(originalMultipliers[i + 1], ladderBillingStructure)
      : Infinity;

    const startOfOverlap = Math.max(shiftStartHour, bracketStart);
    const endOfOverlap = Math.min(shiftEndHour, nextBracketStart);
    const hoursInBracket = Math.max(0, endOfOverlap - startOfOverlap);

    if (hoursInBracket <= 0) continue;

    const bracketPay = hoursInBracket * hourlyRateForCalc * multiplier.defaultMultiplier!;
    totalPay += bracketPay;

    if (multiplier.defaultMultiplier! > 1.0) {
      premiumPay += hoursInBracket * hourlyRateForCalc * (multiplier.defaultMultiplier! - 1.0);
      overtimeHours += hoursInBracket;
    } else {
      straightTimeHours += hoursInBracket;
    }
  }

  // For day rate, ensure minimum guaranteed pay
  if (isDayRate && totalPay < exampleRate) {
    const guaranteedPremium = exampleRate - totalPay;
    totalPay = exampleRate;
    premiumPay += guaranteedPremium;
  }

  return {
    totalPay,
    premiumPay,
    straightTimeHours,
    overtimeHours
  };
}

/**
 * Generates a string representation of the multiplier ladder
 */
export function getMultiplierListString(
  rateMultipliers: RateMultiplier[],
  billingStructure: 'day_rate' | 'hourly' | null,
  vertical: boolean = false,
  offset: number = 1
): string {
  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);

  const allMultipliers = [straightTimeMultiplier, ...ladder];
  const multiplierPairs: string[] = [];

  for (let i = 0; i < allMultipliers.length; i++) {
    const current = allMultipliers[i];
    const nextIndex = i + offset;
    const next = nextIndex < allMultipliers.length ? allMultipliers[nextIndex] : null;

    if (next) {
      multiplierPairs.push(`${current.name} (${current.defaultMultiplier}x) → ${next.name} (${next.defaultMultiplier}x)`);
    } else {
      // For the last multiplier, show it without an arrow
      multiplierPairs.push(`${current.name} (${current.defaultMultiplier}x)`);
    }
  }

  return vertical ? multiplierPairs.join('<br/>') : multiplierPairs.join('<br/>');
}

/**
 * Generates a horizontal inline string representation of the multiplier ladder without surcharges
 */
export function getMultiplierListStringHorizontal(
  rateMultipliers: RateMultiplier[],
  billingStructure: 'day_rate' | 'hourly' | null,
  minimumBillableHours?: number | null
): string {
  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  
  if (ladder.length === 0) {
    return `${straightTimeMultiplier.name} (${straightTimeMultiplier.defaultMultiplier}x)`;
  }
  
  // The ladder is already sorted correctly. Determine its type (daily vs weekly)
  // by inspecting the first overtime multiplier. This tells us which threshold to display.
  const firstOtMultiplier = ladder[0];
  let ladderType: 'day_rate' | 'hourly';

  if (billingStructure === 'hourly') {
    // For an hourly role, we could have gotten a per-shift ladder if enabled.
    if (firstOtMultiplier.defaultPerShiftThresholdHours != null && firstOtMultiplier.perShiftBillingStructures?.includes('hourly')) {
        ladderType = 'day_rate'; // It's a per-shift ladder
    } else {
        ladderType = 'hourly'; // It's a weekly ladder
    }
  } else if (billingStructure === 'day_rate') {
      // For a day_rate role, getSortedOvertimeMultipliers prioritizes the daily ladder.
      // So, if the first OT multiplier has a daily threshold, it must be a daily ladder.
      if (firstOtMultiplier.defaultPerShiftThresholdHours != null) {
          ladderType = 'day_rate'; // It's a per-shift ladder
      } else {
          ladderType = 'hourly'; // It must be the weekly ladder fallback.
      }
  } else {
      // Fallback for null billing structure
      ladderType = 'day_rate'; 
  }
  
  const allMultipliers = [straightTimeMultiplier, ...ladder];
  const multiplierStrings = allMultipliers.map((m, index) => {
    const threshold = getThreshold(m, ladderType);
    const nextThreshold = index + 1 < allMultipliers.length 
      ? getThreshold(allMultipliers[index + 1], ladderType)
      : null;
    
    let rangeText: string;
    if (threshold === 0) {
      const upper = nextThreshold ?? (billingStructure === 'day_rate' ? (minimumBillableHours ?? 8) : null);
      rangeText = upper ? `0-${upper}h` : '0h+';
    } else {
      rangeText = nextThreshold ? `${threshold}-${nextThreshold}h` : `${threshold}h+`;
    }
    
    return `${m.name} (${m.defaultMultiplier}x, ${rangeText})`;
  });
  
  return multiplierStrings.join(' → ');
}

/**
 * Determines the prevailing rate multiplier at a specific hour of work
 */
export function getPrevailingRateMultiplier(
  hoursWorked: number,
  rateMultipliers: RateMultiplier[],
  billingStructure: 'day_rate' | 'hourly' | null
): RateMultiplier {
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  
  if (ladder.length === 0) {
    return straightTimeMultiplier;
  }
  
  // Find the highest threshold that has been reached from the pre-calculated ladder
  let applicableMultiplier = straightTimeMultiplier;
  const firstOtMultiplier = ladder[0];
  if (!firstOtMultiplier) {
    // No OT multipliers, so it doesn't matter
    return straightTimeMultiplier;
  }
  
  // The ladder is already correctly sorted by getSortedOvertimeMultipliers.
  // We just need to know which threshold property to read from it.
  // If the first item in the ladder has a day rate threshold, the whole ladder is a day rate ladder.
  const isDailyLadder = firstOtMultiplier.defaultPerShiftThresholdHours != null;

  for (const multiplier of ladder) {    
    const threshold = isDailyLadder ? multiplier.defaultPerShiftThresholdHours : multiplier.defaultPerWeekThresholdHours;
    if (threshold === null || threshold === undefined) continue; // Should be filtered by getSortedOvertimeMultipliers, but as a safeguard
    if (threshold > 0 && hoursWorked >= threshold) {
      applicableMultiplier = multiplier;
    }
    // Don't break here - continue checking all multipliers to find the highest applicable one
  }
  
  return applicableMultiplier;
}

/**
 * Generates rate progression information for a given number of work hours
 */
export function getRateProgressionInfo(
  workHours: number,
  context: RateCalculationContext,
  surchargeOptions: SurchargeOptions = {}
): RateProgressionResult {
  const { rateMultipliers, minimumBillableHours } = context;
  const sampleRole = resolveRole(context);
  const billingStructure = sampleRole.billingStructure;
  const exampleRate = sampleRole.rate;
  const isDayRate = sampleRole.billingStructure === 'day_rate';

  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const originalMultipliers = [straightTimeMultiplier, ...ladder];

  const ladderTopMultiplier = Math.max(0, ...originalMultipliers.map(m => m.defaultMultiplier ?? 0));
  const normalizedExtended = normalizeExtendedTiers(
    surchargeOptions.extendedOvertimeTiers ?? [],
    ladderTopMultiplier,
  );
  const lookupLadder: RateMultiplier[] = [
    ...originalMultipliers,
    ...normalizedExtended.map((t): RateMultiplier => ({
      code: `__ext_${t.sortOrder}`,
      name: t.name,
      defaultMultiplier: t.rate,
      defaultPerShiftThresholdHours: null,
      defaultPerWeekThresholdHours: null,
      perShiftBillingStructures: null,
      perWeekBillingStructures: null,
    })),
  ];

  // Determine the type of ladder to correctly read thresholds.
  let ladderBillingStructure: 'day_rate' | 'hourly';
  if (ladder.length > 0) {
    const firstOtMultiplier = ladder[0];
    // If the first multiplier in the ladder has a day rate threshold, it's a daily ladder.
    if (firstOtMultiplier.defaultPerShiftThresholdHours != null) {
      ladderBillingStructure = 'day_rate';
    } else {
      ladderBillingStructure = 'hourly';
    }
  } else {
    // If no ladder, it doesn't matter much, but we need a value.
    ladderBillingStructure = billingStructure || 'day_rate';
  }

  const dayRateGuaranteedHours = isDayRate ? (minimumBillableHours ?? 8) : 0;
  const hourlyRateForCalc = isDayRate ? (dayRateGuaranteedHours > 0 ? exampleRate / dayRateGuaranteedHours : exampleRate) : exampleRate;

  const progressionParts: string[] = [];
  const progressionWithPayParts: string[] = [];
  const segments: PayBucket[] = [];
  let lastMultiplier: RateMultiplier | { code: string; name: string; defaultMultiplier: number; } = straightTimeMultiplier;
  let firstMultiplier: (RateMultiplier | { code: string; name: string; defaultMultiplier: number; }) | null = null;
  let floorWasEnforced = false;

  // For daily ladders, thresholds reset each shift/day; for weekly ladders, include the weekly offset.
  const shiftStartBase = ladderBillingStructure === 'hourly' ? (surchargeOptions.startingHoursInWeek || 0) : 0;
  const violationOnly = surchargeOptions.surchargeAppliesToShortedHoursOnly ?? false;
  const violationHours = (violationOnly && surchargeOptions.violationHours)
    ? surchargeOptions.violationHours
    : workHours;

  let effectiveStartIndex = 0;
  if (surchargeOptions.mode === 'offset_overtime' && surchargeOptions.fixedStartingMultiplierCode) {
    const fixedIndex = originalMultipliers.findIndex(m => m.code === surchargeOptions.fixedStartingMultiplierCode);
    if (fixedIndex > 0) { // must be > 0 because index 0 is straight time
        effectiveStartIndex = fixedIndex;
    }
  } else if (surchargeOptions.mode && surchargeOptions.previousShiftFinalRateCode && (surchargeOptions.mode === 'carry_over' || surchargeOptions.mode === 'bump_up')) {
    const prevIndex = originalMultipliers.findIndex(m => m.code === surchargeOptions.previousShiftFinalRateCode);
    
    if (prevIndex !== -1) {
      if (surchargeOptions.mode === 'carry_over') {
        effectiveStartIndex = (prevIndex === 0) ? 1 : prevIndex;
      } else if (surchargeOptions.mode === 'bump_up') {
        effectiveStartIndex = prevIndex + 1;
      }
    }
  }
  effectiveStartIndex = Math.min(effectiveStartIndex, originalMultipliers.length - 1);

  for (let i = 0; i < originalMultipliers.length; i++) {
    const naturalMultiplier = originalMultipliers[i];
    const bracketStart = getThreshold(naturalMultiplier, ladderBillingStructure);
    const shiftStartHour = shiftStartBase;
    const shiftEndHour = shiftStartBase + workHours;

    if (shiftEndHour <= bracketStart) continue;

    const nextBracketStart = (i + 1 < originalMultipliers.length)
      ? getThreshold(originalMultipliers[i + 1], ladderBillingStructure)
      : Infinity;

    const startOfOverlap = Math.max(shiftStartHour, bracketStart);
    const endOfOverlap = Math.min(shiftEndHour, nextBracketStart);
    const hoursInBracket = Math.max(0, endOfOverlap - startOfOverlap);

    if (hoursInBracket <= 0) continue;

    // Split hours into penalized and normal
    const penalizedHoursStart = shiftStartBase;
    const penalizedHoursEnd = shiftStartBase + violationHours;
    
    const penalizedOverlapStart = Math.max(startOfOverlap, penalizedHoursStart);
    const penalizedOverlapEnd = Math.min(endOfOverlap, penalizedHoursEnd);
    const penalizedHours = Math.max(0, penalizedOverlapEnd - penalizedOverlapStart);

    const normalHours = hoursInBracket - penalizedHours;

    // Process penalized hours
    if (penalizedHours > 0) {
      let multiplierToUse = naturalMultiplier;
      let annotation = '';
      let payAnnotation = '';
      let liftedFromTierName: string | undefined;

      if (surchargeOptions.mode === 'guaranteed_rate' && surchargeOptions.fixedStartingMultiplierCode) {
        const surchargeFloorMultiplier = rateMultipliers.find(m => m.code === surchargeOptions.fixedStartingMultiplierCode);
        if (surchargeFloorMultiplier) {
          if (naturalMultiplier.defaultMultiplier! < surchargeFloorMultiplier.defaultMultiplier!) {
            liftedFromTierName = naturalMultiplier.name;
            multiplierToUse = surchargeFloorMultiplier;
            annotation = ' (floor enforced)';
            payAnnotation = ' - Floor Enforced';
            floorWasEnforced = true;
          } else if (floorWasEnforced) {
            annotation = ' (natural progression)';
            payAnnotation = ' - Natural Progression';
            floorWasEnforced = false;
          }
        }
      } else if (surchargeOptions.mode === 'carry_over' || surchargeOptions.mode === 'bump_up' || surchargeOptions.mode === 'offset_overtime') {
        const effectiveMultiplierIndex = i + effectiveStartIndex;
        if (effectiveMultiplierIndex < lookupLadder.length) {
            multiplierToUse = lookupLadder[effectiveMultiplierIndex];
        } else {
            multiplierToUse = lookupLadder[lookupLadder.length - 1];
        }
      }

      if (!firstMultiplier) {
        firstMultiplier = multiplierToUse;
      }
      
      const multiplierText = `${formatMultiplier(multiplierToUse as RateMultiplier)}${annotation}`;
      const multiplierWithPayText = `${formatMultiplierWithPay(multiplierToUse as RateMultiplier, penalizedHours, hourlyRateForCalc)}${payAnnotation}`;
      
      progressionParts.push(`${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${multiplierText}**`);
      progressionWithPayParts.push(`${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${multiplierWithPayText}**`);
      segments.push({
        hours: penalizedHours,
        tierName: multiplierToUse.name,
        multiplier: multiplierToUse.defaultMultiplier ?? 1,
        amount: penalizedHours * hourlyRateForCalc * (multiplierToUse.defaultMultiplier ?? 1),
        ...(payAnnotation ? { annotation: payAnnotation } : {}),
        ...(liftedFromTierName ? { lifted: true, liftedFrom: liftedFromTierName } : {}),
      });
      lastMultiplier = multiplierToUse;
    }

    // Process normal hours
    if (normalHours > 0) {
      const multiplierToUse = naturalMultiplier;

      if (!firstMultiplier) {
        firstMultiplier = multiplierToUse;
      }
      
      // Add natural progression annotation for multiplier_floor mode when floor was enforced earlier
      let annotation = '';
      let payAnnotation = '';
      if (surchargeOptions.mode === 'guaranteed_rate' && floorWasEnforced) {
        const surchargeFloorMultiplier = rateMultipliers.find(m => m.code === surchargeOptions.fixedStartingMultiplierCode);
        if (surchargeFloorMultiplier && naturalMultiplier.defaultMultiplier! >= surchargeFloorMultiplier.defaultMultiplier!) {
          annotation = ' (natural progression)';
          payAnnotation = ' - Natural Progression';
          floorWasEnforced = false;
        }
      }
      
      const multiplierText = `${formatMultiplier(multiplierToUse as RateMultiplier)}${annotation}`;
      const multiplierWithPayText = `${formatMultiplierWithPay(multiplierToUse as RateMultiplier, normalHours, hourlyRateForCalc)}${payAnnotation}`;
      
      progressionParts.push(`${normalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${multiplierText}**`);
      progressionWithPayParts.push(`${normalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${multiplierWithPayText}**`);
      segments.push({
        hours: normalHours,
        tierName: multiplierToUse.name,
        multiplier: multiplierToUse.defaultMultiplier ?? 1,
        amount: normalHours * hourlyRateForCalc * (multiplierToUse.defaultMultiplier ?? 1),
        ...(payAnnotation ? { annotation: payAnnotation } : {}),
      });
      lastMultiplier = multiplierToUse;
    }
  }

  // For multiplier_floor mode, don't merge parts to show the threshold splits clearly
  const mergedProgressionParts = surchargeOptions.mode === 'guaranteed_rate' 
    ? progressionParts 
    : progressionParts.reduce((acc, part) => {
        const lastPart = acc[acc.length - 1];
        if (lastPart && lastPart.split('@')[1] === part.split('@')[1]) {
          const currentHours = parseFloat(part.split(' ')[0]);
          const lastHours = parseFloat(lastPart.split(' ')[0]);
          const totalHours = lastHours + currentHours;
          acc[acc.length - 1] = `${totalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ ${part.split('@')[1]}`;
        } else {
          acc.push(part);
        }
        return acc;
      }, [] as string[]);

  const mergedProgressionWithPayParts = surchargeOptions.mode === 'guaranteed_rate'
    ? progressionWithPayParts
    : progressionWithPayParts.reduce((acc, part) => {
        const lastPart = acc[acc.length - 1];
        // Extract the multiplier name from both parts to compare
        const getMultiplierName = (str: string) => {
          const match = str.match(/\*\*([^(]+)\(/);
          return match ? match[1].trim() : '';
        };
        
        if (lastPart && getMultiplierName(lastPart) === getMultiplierName(part)) {
          const currentHours = parseFloat(part.split(' ')[0]);
          const lastHours = parseFloat(lastPart.split(' ')[0]);
          const totalHours = lastHours + currentHours;
          
          const currentPay = parseFloat(part.split('$')[1].split(')')[0]);
          const lastPay = parseFloat(lastPart.split('$')[1].split(')')[0]);
          const totalPay = lastPay + currentPay;
          
          const multiplierName = getMultiplierName(part);
          acc[acc.length - 1] = `${totalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${multiplierName} ($${totalPay.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})**`;
        } else {
          acc.push(part);
        }
        return acc;
      }, [] as string[]);

  const mergedSegments = surchargeOptions.mode === 'guaranteed_rate'
    ? segments
    : segments.reduce((acc, seg) => {
        const last = acc[acc.length - 1];
        if (last && last.tierName === seg.tierName && last.multiplier === seg.multiplier) {
          acc[acc.length - 1] = {
            ...last,
            hours: last.hours + seg.hours,
            amount: last.amount + seg.amount,
          };
        } else {
          acc.push({ ...seg });
        }
        return acc;
      }, [] as PayBucket[]);

  const finalFirstMultiplier = firstMultiplier || straightTimeMultiplier;
  return {
    progression: mergedProgressionParts.join('<br/>↓<br/>') || `${workHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${formatMultiplier(straightTimeMultiplier as RateMultiplier)}**`,
    progressionWithPay: mergedProgressionWithPayParts.join('<br/>↓<br/>') || `${workHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hours @ **${formatMultiplierWithPay(straightTimeMultiplier as RateMultiplier, workHours, hourlyRateForCalc)}**`,
    finalRate: formatMultiplier(lastMultiplier as RateMultiplier),
    finalRateCode: lastMultiplier.code || 'STRAIGHT_TIME',
    startingRate: formatMultiplier(finalFirstMultiplier as RateMultiplier),
    startingRateCode: finalFirstMultiplier.code || 'STRAIGHT_TIME',
    segments: mergedSegments,
  };
}

export type PayCalculationRow = {
  hours_worked: string;
  rate_name: string;
  multiplier: string;
  calculation: string;
  gross_pay: string;
};

export function generatePayCalculationRows(
  workHours: number,
  context: RateCalculationContext
): PayCalculationRow[] {
  const { rateMultipliers, minimumBillableHours } = context;
  const sampleRole = resolveRole(context);
  const billingStructure = sampleRole.billingStructure;

  const hasDailyThresholds = rateMultipliers.some(m => m.defaultPerShiftThresholdHours != null);
  const ladderBillingStructure = hasDailyThresholds ? 'day_rate' : 'hourly';

  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const allMultipliers = [straightTimeMultiplier, ...ladder];

  const dayRateGuaranteedHours = billingStructure === 'day_rate' ? (minimumBillableHours ?? 8) : 0;
  const baseHourlyRate = billingStructure === 'day_rate' && sampleRole.rate && dayRateGuaranteedHours > 0
    ? sampleRole.rate / dayRateGuaranteedHours
    : (sampleRole.rate || 50);

  const rows: PayCalculationRow[] = [];

  for (let i = 0; i < allMultipliers.length; i++) {
    const currentMultiplier = allMultipliers[i];
    const bracketStart = getThreshold(currentMultiplier, ladderBillingStructure);

    if (workHours <= bracketStart) continue;

    const nextBracketStart = (i + 1 < allMultipliers.length)
      ? getThreshold(allMultipliers[i + 1], ladderBillingStructure)
      : Infinity;
    
    const hoursInBracket = Math.min(workHours, nextBracketStart) - bracketStart;
    if (hoursInBracket <= 0) continue;
    
    const payRate = baseHourlyRate * currentMultiplier.defaultMultiplier!;
    const grossPay = hoursInBracket * payRate;

    rows.push({
      hours_worked: `${hoursInBracket.toLocaleString(undefined, { maximumFractionDigits: 2 })}h`,
      rate_name: currentMultiplier.name,
      multiplier: `${currentMultiplier.defaultMultiplier!}x`,
      calculation: `$${payRate.toLocaleString(undefined, { minimumFractionDigits: payRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`,
      gross_pay: `$${grossPay.toLocaleString(undefined, { minimumFractionDigits: grossPay % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`
    });
  }
  return rows;
}

export type PremiumPayCalculationRow = {
  hours_worked: string;
  rate_name: string;
  multiplier: string;
  base_hourly_rate: string;
  prevailing_hourly_rate: string;
  premium_basis?: string;
  base_gross: string;
  hourly_premium_rate: string;
  premium_amount: string;
  final_gross: string;
  // For short_turn compatibility
  uplift_per_hour?: string;
  total_hourly_rate?: string;
  gross_pay?: string;
  base_calculation?: string;
  premium_calculation?: string;
  base_rate?: string;
  multiplied_rate?: string;
  effective_hourly_rate?: string;
};

export interface PremiumOptions {
  premiumType: 'percentage' | 'fixed';
  premiumValue?: number | null;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  surchargeAppliesToShortedHoursOnly?: boolean;
  violationHours?: number;
  surchargeStartsAfterHours?: number;
}

export function generatePremiumPayCalculationRows(
  workHours: number,
  context: RateCalculationContext,
  premiumOptions: PremiumOptions
): PremiumPayCalculationRow[] {
  const { rateMultipliers, minimumBillableHours } = context;
  const sampleRole = resolveRole(context);
  const billingStructure = sampleRole.billingStructure;

  const straightTimeMultiplier = getStraightTimeMultiplier(rateMultipliers);
  const ladder = getSortedOvertimeMultipliers(rateMultipliers, billingStructure);
  const allMultipliers = [straightTimeMultiplier, ...ladder];
  
  // Determine the type of ladder to correctly read thresholds.
  let ladderBillingStructure: 'day_rate' | 'hourly';
  if (ladder.length > 0) {
    const firstOtMultiplier = ladder[0];
    // If the first multiplier in the ladder has a day rate threshold, it's a daily ladder.
    if (firstOtMultiplier.defaultPerShiftThresholdHours != null) {
      ladderBillingStructure = 'day_rate';
    } else {
      ladderBillingStructure = 'hourly';
    }
  } else {
    // If no ladder, it doesn't matter much, but we need a value.
    ladderBillingStructure = billingStructure || 'day_rate';
  }

  const dayRateGuaranteedHours = billingStructure === 'day_rate' ? (minimumBillableHours ?? 8) : 0;
  const baseHourlyRate = billingStructure === 'day_rate' && sampleRole.rate && dayRateGuaranteedHours > 0
    ? sampleRole.rate / dayRateGuaranteedHours
    : (sampleRole.rate || 50);

  const rows: PremiumPayCalculationRow[] = [];
  const {
    premiumType,
    premiumValue,
    premiumBasis,
    surchargeAppliesToShortedHoursOnly,
    violationHours,
    surchargeStartsAfterHours
  } = premiumOptions;

  const effectiveViolationHours = surchargeAppliesToShortedHoursOnly ? (violationHours ?? workHours) : workHours;

  for (let i = 0; i < allMultipliers.length; i++) {
    const currentMultiplier = allMultipliers[i];
    const bracketStart = getThreshold(currentMultiplier, ladderBillingStructure);

    if (workHours <= bracketStart) continue;

    const nextBracketStart = (i + 1 < allMultipliers.length)
      ? getThreshold(allMultipliers[i + 1], ladderBillingStructure)
      : Infinity;
    
    const hoursInBracket = Math.min(workHours, nextBracketStart) - bracketStart;
    if (hoursInBracket <= 0) continue;

    const bracketEnd = bracketStart + hoursInBracket;
    
    let penalizedHours = 0;
    let normalHours = 0;

    if (surchargeStartsAfterHours != null && surchargeStartsAfterHours >= 0) {
      // Meal surcharge logic: surcharge applies AFTER a threshold
      const surchargeStartInBracket = Math.max(bracketStart, surchargeStartsAfterHours);
      penalizedHours = Math.max(0, bracketEnd - surchargeStartInBracket);
      normalHours = hoursInBracket - penalizedHours;
    } else if (surchargeAppliesToShortedHoursOnly && violationHours != null) {
      // Short turn violation-only logic: surcharge applies for a DURATION from the start
      const surchargeEndInBracket = Math.min(bracketEnd, violationHours);
      penalizedHours = Math.max(0, surchargeEndInBracket - bracketStart);
      normalHours = hoursInBracket - penalizedHours;
    } else {
      // Default: surcharge applies to all hours in the bracket
      penalizedHours = hoursInBracket;
      normalHours = 0;
    }

    const prevailingHourlyRate = baseHourlyRate * currentMultiplier.defaultMultiplier!;

    if (normalHours > 0) {
      const normalStart = bracketStart;
      const normalEnd = bracketStart + normalHours;
      const baseGross = normalHours * prevailingHourlyRate;
      
      // For percentage premium, determine the premium basis display
      let premiumBasisStr = '';
      if (premiumType === 'percentage') {
        const basisRate = premiumBasis === 'ot_inclusive' ? prevailingHourlyRate : baseHourlyRate;
        if (premiumBasis === 'ot_inclusive') {
          premiumBasisStr = `$${basisRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>($${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${currentMultiplier.defaultMultiplier}x)`;
        } else {
          premiumBasisStr = `$${basisRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr`;
        }
      }

      rows.push({
        hours_worked: `${normalStart.toLocaleString(undefined, { maximumFractionDigits: 2 })}-${normalEnd.toLocaleString(undefined, { maximumFractionDigits: 2 })}h`,
        rate_name: currentMultiplier.name,
        multiplier: `${currentMultiplier.defaultMultiplier}x`,
        base_hourly_rate: `$${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        prevailing_hourly_rate: `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        premium_basis: premiumBasisStr,
        base_gross: `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>(${normalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${currentMultiplier.defaultMultiplier}x)`,
        hourly_premium_rate: 'No Violation',
        premium_amount: '$0.00',
        final_gross: `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>(${normalHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`,
        uplift_per_hour: '0.00',
        total_hourly_rate: prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        gross_pay: `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        base_calculation: `$${(baseHourlyRate * currentMultiplier.defaultMultiplier!).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        premium_calculation: 'No Violation',
        base_rate: `$${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        multiplied_rate: `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        effective_hourly_rate: `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} + $0.00)`,
      });
    }

    if (penalizedHours > 0) {
      const penalizedStart = bracketStart + normalHours;
      const penalizedEnd = bracketEnd;
      const baseGross = penalizedHours * prevailingHourlyRate;
      let premium = 0;
      let hourlyPremiumRateStr = '';
      let premiumAmountWithCalc = '';
      let uplift_per_hour = 0;
      let premium_calculation_str = '';

      if (premiumBasis === 'ot_inclusive') { // Compounding
        if (premiumType === 'percentage') {
          const premiumRate = prevailingHourlyRate * (Number(premiumValue ?? 0) / 100);
          premium = baseGross * (Number(premiumValue ?? 0) / 100);
          uplift_per_hour = premiumRate;
          hourlyPremiumRateStr = `$${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>(${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${Number(premiumValue ?? 0)}%)`;
          premiumAmountWithCalc = `${premium.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h)`;
          premium_calculation_str = `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${Number(premiumValue || 0)}%`;
        } else { // fixed (Dollar) + ot_inclusive compounds by active OT multiplier
          const basePremiumRate = (Number(premiumValue) || 15);
          const premiumRate = basePremiumRate * currentMultiplier.defaultMultiplier!;
          premium = penalizedHours * premiumRate;
          uplift_per_hour = premiumRate;
          hourlyPremiumRateStr = `$${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>($${basePremiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${currentMultiplier.defaultMultiplier}x)`;
          premiumAmountWithCalc = `${premium.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${basePremiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${currentMultiplier.defaultMultiplier}x × ${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h)`;
          premium_calculation_str = `$${basePremiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${currentMultiplier.defaultMultiplier}x`;
        }
      } else { // Additive
        if (premiumType === 'percentage') {
          const premiumRate = baseHourlyRate * (Number(premiumValue ?? 0) / 100);
          premium = penalizedHours * premiumRate;
          uplift_per_hour = premiumRate;
          hourlyPremiumRateStr = `$${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>(${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${Number(premiumValue ?? 0)}%)`;
          premiumAmountWithCalc = `${premium.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h)`;
          premium_calculation_str = `$${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${Number(premiumValue || 0)}%`;
        } else { // fixed
          const premiumRate = (Number(premiumValue) || 15);
          premium = penalizedHours * premiumRate;
          uplift_per_hour = premiumRate;
          hourlyPremiumRateStr = `$${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr`;
          premiumAmountWithCalc = `${premium.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>($${premiumRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr × ${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h)`;
          premium_calculation_str = `$${(Number(premiumValue) || 15).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
        }
      }
      
      const baseGrossCalculation = premiumBasis === 'ot_inclusive'
        ? `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: baseGross % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}<br/>(${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: baseHourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} × ${currentMultiplier.defaultMultiplier}x)`
        : `$${baseGross.toLocaleString(undefined, { minimumFractionDigits: baseGross % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}<br/>(${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: baseHourlyRate % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} × ${currentMultiplier.defaultMultiplier}x)`;

      const totalHourlyRate = prevailingHourlyRate + uplift_per_hour;
      const finalGross = baseGross + premium;

      // For percentage premium, determine the premium basis display
      let premiumBasisStrPenalized = '';
      if (premiumType === 'percentage') {
        const basisRate = premiumBasis === 'ot_inclusive' ? prevailingHourlyRate : baseHourlyRate;
        if (premiumBasis === 'ot_inclusive') {
          premiumBasisStrPenalized = `$${basisRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>($${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} × ${currentMultiplier.defaultMultiplier}x)`;
        } else {
          premiumBasisStrPenalized = `$${basisRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr`;
        }
      }

      // Create effective hourly rate display with calculation
      const effectiveHourlyRateDisplay = `$${totalHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}/hr<br/>($${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} + $${uplift_per_hour.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`;

      rows.push({
        hours_worked: `${penalizedStart.toLocaleString(undefined, { maximumFractionDigits: 2 })}-${penalizedEnd.toLocaleString(undefined, { maximumFractionDigits: 2 })}h`,
        rate_name: currentMultiplier.name,
        multiplier: `${currentMultiplier.defaultMultiplier}x`,
        base_hourly_rate: `$${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        prevailing_hourly_rate: `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        premium_basis: premiumBasisStrPenalized,
        base_gross: baseGrossCalculation,
        hourly_premium_rate: hourlyPremiumRateStr,
        premium_amount: `${premiumAmountWithCalc}<br/>(${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${uplift_per_hour.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`,
        final_gross: `$${finalGross.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}<br/>(${penalizedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })}h @ $${totalHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })})`,
        uplift_per_hour: uplift_per_hour.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        total_hourly_rate: totalHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }),
        gross_pay: `$${finalGross.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        base_calculation: `$${(baseHourlyRate * currentMultiplier.defaultMultiplier!).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        premium_calculation: premium_calculation_str,
        base_rate: `$${baseHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        multiplied_rate: `$${prevailingHourlyRate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
        effective_hourly_rate: effectiveHourlyRateDisplay,
      });
    }
  }

  return rows;
}
